import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Checkbox, Input, Select, type Column } from "@legal-500/legal500-design-system";
import {
  PartialBatchError,
  powerlistEditions,
  powerlistEntries,
  type Edition,
  type Entry,
  type EntryStatus,
  type PowerlistList,
} from "../../../api/powerlist.service";
import { DataTable } from "../../../components/DataTable/DataTable";
import { ErrorBanner } from "../../../components/ErrorBanner/ErrorBanner";
import SelectAllCheckbox from "../../../components/Form/SelectAllCheckbox/SelectAllCheckbox";
import LoadingState from "../../../components/LoadingState";
import { StatusBadge } from "../../../components/StatusBadge";
import { useQuery } from "../../../hooks/useApi";
import { ROUTES } from "../../../routes";
import { bySurname, entryMatchesSearch, usePowerlistAccess, useToast } from "../shared";
import styles from "../Powerlist.module.css";
import { CopyEntriesModal, type CopyOutcome } from "./CopyEntriesModal";

type EntriesTabProps = { editionId: string; seriesId: string; lists: PowerlistList[] };

function entriesLabel(count: number): string {
  return `${count} ${count === 1 ? "entry" : "entries"}`;
}

export function EntriesTab({ editionId, seriesId, lists }: EntriesTabProps) {
  const { canWrite, canPublish } = usePowerlistAccess();
  const canBulk = canWrite && canPublish;
  const { toast, success, failure } = useToast();
  const { data, loading, error, refetch, isRefetching } = useQuery(
    () => powerlistEntries.listForEdition(editionId),
    [editionId],
  );
  const [listFilter, setListFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyReport, setCopyReport] = useState<CopyOutcome | null>(null);
  const { data: seriesEditions } = useQuery(
    () => (canWrite ? powerlistEditions.listForSeries(seriesId) : Promise.resolve<Edition[]>([])),
    [seriesId, canWrite],
  );
  const otherEditions = (seriesEditions ?? []).filter(edition => edition.id !== editionId);
  const listTitles = useMemo(() => new Map(lists.map(list => [list.id, list.title])), [lists]);

  const entries = useMemo(() => {
    return (data ?? [])
      .filter(entry => !listFilter || entry.listId === listFilter)
      .filter(entry => entryMatchesSearch(entry, search))
      .sort(bySurname);
  }, [data, listFilter, search]);

  const selectedEntries = entries.filter(entry => selected.has(entry.id));
  const allSelected = entries.length > 0 && selectedEntries.length === entries.length;

  function toggleEntry(id: string) {
    setSelected(previous => {
      const next = new Set(previous);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(entries.map(entry => entry.id)));
  }

  async function applyBulkStatus(status: EntryStatus) {
    setBulkBusy(true);
    const entryIds = selectedEntries.map(entry => entry.id);
    const outcome = status === "published" ? "published" : "moved to draft";
    try {
      const updated = await powerlistEntries.bulkStatus(editionId, entryIds, status);
      success(`${entriesLabel(updated)} ${outcome}`);
      setSelected(new Set());
    } catch (bulkError) {
      failure(
        bulkError instanceof PartialBatchError
          ? `Only ${bulkError.completed} of ${entriesLabel(entryIds.length)} ${outcome} before an error: ${bulkError.message}`
          : bulkError,
      );
    } finally {
      await refetch();
      setBulkBusy(false);
    }
  }

  const selectionColumn: Column<Entry> = {
    header: (
      <SelectAllCheckbox
        aria-label="Select all entries"
        checked={allSelected}
        indeterminate={selectedEntries.length > 0 && !allSelected}
        disabled={entries.length === 0 || bulkBusy}
        onChange={toggleAll}
      />
    ) as unknown as string,
    accessor: "id",
    render: (_value: unknown, row: Entry) => (
      <Checkbox
        aria-label={`Select ${row.name}`}
        checked={selected.has(row.id)}
        disabled={bulkBusy}
        onChange={() => toggleEntry(row.id)}
      />
    ),
  };

  const columns: Column<Entry>[] = [
    ...(canBulk ? [selectionColumn] : []),
    {
      header: "Name",
      accessor: "name",
      render: (_value: unknown, row: Entry) => (
        <Link to={ROUTES.powerlist.entry(editionId, row.id)}>{row.name}</Link>
      ),
    },
    {
      header: "List",
      accessor: "listId",
      render: (_value: unknown, row: Entry) => listTitles.get(row.listId) ?? "—",
    },
    {
      header: "Job title",
      accessor: "jobTitle",
      render: (_value: unknown, row: Entry) => row.jobTitle ?? "—",
    },
    {
      header: "Company",
      accessor: "companyName",
      render: (_value: unknown, row: Entry) => row.companyName ?? "—",
    },
    {
      header: "Badges",
      accessor: "badges",
      render: (_value: unknown, row: Entry) => (
        <span className={styles.tagList}>
          {row.badges.map(badge => (
            <span key={badge.id} className={styles.tag}>
              {badge.name}
            </span>
          ))}
        </span>
      ),
    },
    {
      header: "Status",
      accessor: "status",
      render: (_value: unknown, row: Entry) => <StatusBadge status={row.status} />,
    },
  ];

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <p className={styles.muted}>
          {data ? `${entries.length} of ${data.length} entries` : "Entries in this edition"}
        </p>
        <div className={styles.actions}>
          {canWrite && otherEditions.length > 0 && (
            <Button variant="outlined" size="small" onClick={() => setCopyOpen(true)}>
              Copy from another edition
            </Button>
          )}
          {canWrite && lists.length > 0 && (
            <Link to={ROUTES.powerlist.newEntry(editionId)}>New entry</Link>
          )}
        </div>
      </div>
      {copyReport && (copyReport.skipped.length > 0 || copyReport.error) && (
        <div className={styles.notice} role="region" aria-label="Entries not copied">
          {copyReport.error && (
            <p className={styles.formError} role="alert">
              The copy stopped after {entriesLabel(copyReport.copied)} because of an error:{" "}
              {copyReport.error}. Copy again to finish; entries already copied are skipped.
            </p>
          )}
          {copyReport.skipped.length > 0 && (
            <>
              <p className={styles.muted}>
                {copyReport.skipped.length === 1
                  ? "1 entry wasn't copied:"
                  : `${copyReport.skipped.length} entries weren't copied:`}
              </p>
              <ul>
                {copyReport.skipped.map(skip => (
                  <li key={skip.entryId}>
                    {skip.name}: {skip.reason}
                  </li>
                ))}
              </ul>
            </>
          )}
          <Button variant="text" size="small" onClick={() => setCopyReport(null)}>
            Dismiss
          </Button>
        </div>
      )}
      {lists.length === 0 && <p className={styles.muted}>Add a list before adding entries.</p>}
      <div className={styles.inlineForm}>
        <Select
          id="powerlist-entries-list"
          label="List"
          value={listFilter}
          onChange={event => setListFilter(event.target.value)}
          options={[
            { value: "", label: "All lists" },
            ...lists.map(list => ({ value: list.id, label: list.title })),
          ]}
          fullWidth
        />
        <Input
          id="powerlist-entries-search"
          label="Search"
          type="search"
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder="Name, job title or company"
          fullWidth
        />
      </div>
      {canBulk && selectedEntries.length > 0 && (
        <div className={styles.bulkBar} role="region" aria-label="Bulk actions">
          <p className={styles.muted} aria-live="polite">
            {entriesLabel(selectedEntries.length)} selected
          </p>
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={bulkBusy}
            onClick={() => void applyBulkStatus("published")}
          >
            Publish
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={bulkBusy}
            onClick={() => void applyBulkStatus("draft")}
          >
            Move to draft
          </Button>
          <Button
            variant="text"
            size="small"
            disabled={bulkBusy}
            onClick={() => setSelected(new Set())}
          >
            Clear selection
          </Button>
        </div>
      )}
      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={isRefetching} />}
      {loading && data === null ? (
        <LoadingState message="Loading entries..." />
      ) : (
        <DataTable data={entries} columns={columns} rowKey="id" emptyMessage="No entries found." />
      )}
      {copyOpen && (
        <CopyEntriesModal
          editionId={editionId}
          otherEditions={otherEditions}
          onClose={() => setCopyOpen(false)}
          onCopied={outcome => {
            setCopyOpen(false);
            setCopyReport(outcome);
            if (outcome.error) failure(`${entriesLabel(outcome.copied)} copied before an error`);
            else success(`${entriesLabel(outcome.copied)} copied`);
            void refetch();
          }}
        />
      )}
      {toast}
    </div>
  );
}
